import { Text, breakpoints } from "@asurion-core/mx-asurion-ui-react";
import {
  Grid,
  Row,
  WorkOrderPrograms,
  WorkOrderStatus,
  WorkorderSearchResultItemCard,
  workorderProgrammNameWithTags,
  workorderStatusWithTag
} from "@ubif/components";
import { SearchWorkordersResponse } from "@ubif/util-store";
import { Fragment } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";

import { CustomerSearchNoResults } from "./elements/CustomerSearchNoResults";
import { ShowMore } from "./elements/ShowMore";

export const WorkorderSearchResults = ({
  workordersToDisplay = [],
  searchedWorkorderData = [],
  workordersCount,
  workorderSelectionClick,
  moreClick,
  debouncedSearchCriteria,
  isSearchSuccessOrError,
  showMoreWorkorders,
  isShowMoreWorkordersPending
}: {
  workordersToDisplay: SearchWorkordersResponse["workOrders"];
  searchedWorkorderData: SearchWorkordersResponse["workOrders"];
  workordersCount: number;
  workorderSelectionClick: (id: number, clientId?: number) => void;
  moreClick: () => void;
  debouncedSearchCriteria: string;
  isSearchSuccessOrError: boolean;
  showMoreWorkorders: boolean;
  isShowMoreWorkordersPending: boolean;
}) => {
  const { t } = useTranslation("root-config");

  return (
    <>
      {workordersToDisplay.length > 1 && (
        <>
          <Row>
            <Text as="h3" size={2} weight="feather">
              {t("GlobalSearch.WorkordersSearchResultsWithCount", {
                count: workordersCount
              })}
            </Text>
          </Row>
          <Row>
            <StyledGrid minWidth={20}>
              {workordersToDisplay.map((workorder) => (
                <Fragment key={workorder.workorderId}>
                  <WorkorderSearchResultItemCard
                    fullName={workorder.customer.fullName}
                    email={workorder.customer.email}
                    phone={workorder.customer.primaryPhoneNumber}
                    canCall={false}
                    canEmail={!!workorder.customer.canEmail}
                    canSms={false}
                    workorderId={workorder.workorderId}
                    claimNumber={workorder.claimNumber}
                    workorderStatus={{
                      id: workorder.workorderStatusId,
                      name: workorder.workorderStatusName,
                      type: workorderStatusWithTag[workorder.workorderStatusName as WorkOrderStatus]
                    }}
                    program={{
                      id: workorder.programId,
                      name: workorder.programName,
                      type: workorderProgrammNameWithTags[workorder.programName as WorkOrderPrograms]
                    }}
                    preferredContactLabel={t("GlobalSearch.ContactPreference")}
                    onClick={workorderSelectionClick}
                  />
                </Fragment>
              ))}
            </StyledGrid>
          </Row>
          <ShowMore
            showMoreLabel={t("Showmore")}
            display={!showMoreWorkorders}
            placeInCenter={true}
            isPending={isShowMoreWorkordersPending}
            onClick={moreClick}
          />
        </>
      )}
      {workordersToDisplay.length <= 1 && searchedWorkorderData?.length === 0 && isSearchSuccessOrError && (
        <CustomerSearchNoResults searchText={debouncedSearchCriteria} />
      )}
    </>
  );
};

const StyledGrid = styled(Grid)`
  grid-template-columns: repeat(1, minmax(20rem, 1fr));

  ${breakpoints.md2} {
    grid-template-columns: repeat(2, minmax(20rem, 1fr));
  }
  ${breakpoints.lg} {
    grid-template-columns: repeat(3, minmax(20rem, 1fr));
  }
  ${breakpoints.xxl} {
    grid-template-columns: repeat(4, minmax(20rem, 1fr));
  }
`;
